import React, { useCallback, useMemo } from 'react'
import { Button, Chip, IconButton, MenuItem, Select, SelectChangeEvent, Tooltip, useTheme } from '@mui/material'
import {
  AddCircleOutline,
  CloseOutlined,
  DeleteOutline,
  DragIndicatorOutlined,
  EditOutlined,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { ITableColumnConfig } from 'src/types/report-designer'
import { IFormSchemaColumn } from 'src/generated-types/form-schema'
import { AppTextField } from '../../AppTextField'
import { PropertyLabel } from './PropertiesShared.styles'
import {
  ColumnSection,
  ColumnSectionHeader,
  ColumnCard,
  ColumnCardHeader,
  ColumnHeaderLeft,
  ColumnNameText,
  ColumnHeaderActions,
  ColumnEditBody,
  ColumnDragHandle,
  ColumnFieldRow,
  RequiredAsterisk,
  ColumnFieldLabel,
  PlaceholderText,
  EmptyColumnsHint,
} from './TableComponentProperties.style'
import { getThemeColor } from 'src/utils'
import { MUIThemeColors } from 'src/mui-theme'
import { AddNewColumnCard } from './AddNewColumnCard'

export interface IColumnSectionPanelProps {
  columns: ITableColumnConfig[]
  isColumnsLoading: boolean
  editingColumnId: string | null
  draftColumn: ITableColumnConfig | null
  dragOverColumnIndex: number | null
  availableFieldsForDraft: IFormSchemaColumn[]
  schemaColumns?: IFormSchemaColumn[]
  onAddColumn: () => void
  onEditColumn: (column: ITableColumnConfig) => void
  onSaveColumn: () => void
  onCancelEdit: () => void
  onDeleteColumn: (id: string) => void
  onDraftFieldSelect: (fieldId: string) => void
  onDraftWidthChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onColumnDragStart: (e: React.DragEvent, index: number) => void
  onColumnDragOver: (e: React.DragEvent, index: number) => void
  onColumnDrop: (e: React.DragEvent, index: number) => void
  onColumnDragEnd: () => void
  hideWidthConfig?: boolean
}

export const ColumnSectionPanel: React.FC<IColumnSectionPanelProps> = ({
  columns,
  isColumnsLoading,
  editingColumnId,
  draftColumn,
  dragOverColumnIndex,
  availableFieldsForDraft,
  schemaColumns,
  onAddColumn,
  onEditColumn,
  onSaveColumn,
  onCancelEdit,
  onDeleteColumn,
  onDraftFieldSelect,
  onDraftWidthChange,
  onColumnDragStart,
  onColumnDragOver,
  onColumnDrop,
  onColumnDragEnd,
  hideWidthConfig,
}) => {
  const { t: translate } = useTranslation(['dynamic-report', 'common'])
  const theme = useTheme()
  const { showConfirmDialog } = useDialogCtx()

  const allSchemaIdentifiers = useMemo(
    () => new Set([...(schemaColumns ?? []).map(f => f.identifier)]),
    [schemaColumns],
  )

  const isAddingNewColumn = editingColumnId !== null && columns.every(col => col.id !== editingColumnId)

  const handleDeleteColumnClick = useCallback(
    (col: ITableColumnConfig) => {
      const isMissing = col.formSchemaFieldId ? !allSchemaIdentifiers.has(col.formSchemaFieldId) : false

      if (isMissing) {
        showConfirmDialog(
          translate('delete-column', { ns: 'dynamic-report' }),
          translate('are-you-sure-delete-missing-column', { ns: 'dynamic-report' }),
          {
            acceptText: translate('yes', { ns: 'common' }),
            cancelText: translate('no', { ns: 'common' }),
          },
          pop => {
            onDeleteColumn(col.id)
            pop()
          },
        )
      } else {
        onDeleteColumn(col.id)
      }
    },
    [allSchemaIdentifiers, showConfirmDialog, translate, onDeleteColumn],
  )

  return (
    <ColumnSection>
      <ColumnSectionHeader>
        <PropertyLabel sx={{ mb: 0 }}>{translate('columns', { ns: 'dynamic-report' })}</PropertyLabel>
        <Tooltip
          title={`${translate(
            availableFieldsForDraft.length === 0
              ? 'no-available-fields'
              : isAddingNewColumn
              ? 'save-current-column'
              : 'add-column',
            {
              ns: 'dynamic-report',
            },
          )}`}
        >
          <span>
            <IconButton
              disabled={isColumnsLoading || availableFieldsForDraft.length === 0 || isAddingNewColumn}
              size="small"
              onClick={onAddColumn}
              color="primary"
            >
              <AddCircleOutline fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </ColumnSectionHeader>

      {columns.length === 0 && !(isAddingNewColumn && draftColumn) && (
        <EmptyColumnsHint>{translate('no-columns', { ns: 'dynamic-report' })}</EmptyColumnsHint>
      )}

      {columns.map((column, index) => {
        const isEditing = editingColumnId === column.id
        const workingColumn = isEditing && draftColumn ? draftColumn : column
        const isColumnMissing = column.formSchemaFieldId ? !allSchemaIdentifiers.has(column.formSchemaFieldId) : false

        return (
          <ColumnCard
            key={column.id}
            draggable={!isEditing}
            isDragOver={dragOverColumnIndex === index}
            isEditing={isEditing}
            onDragStart={isEditing ? undefined : e => onColumnDragStart(e, index)}
            onDragOver={isEditing ? undefined : e => onColumnDragOver(e, index)}
            onDrop={isEditing ? undefined : e => onColumnDrop(e, index)}
            onDragEnd={isEditing ? undefined : onColumnDragEnd}
          >
            <ColumnCardHeader>
              <ColumnHeaderLeft>
                {!isEditing && (
                  <Tooltip title={`${translate('drag-to-reorder', { ns: 'dynamic-report' })}`}>
                    <ColumnDragHandle>
                      <DragIndicatorOutlined
                        fontSize="small"
                        sx={{
                          color: isColumnMissing
                            ? getThemeColor(theme, MUIThemeColors.errorMain)
                            : getThemeColor(theme, MUIThemeColors.secondaryMain),
                        }}
                      />
                    </ColumnDragHandle>
                  </Tooltip>
                )}
                <Chip
                  label={`#${column.orderNumber}`}
                  size="small"
                  sx={{
                    fontSize: '0.7rem',
                    height: 20,
                    fontWeight: 600,
                    color: isColumnMissing
                      ? getThemeColor(theme, MUIThemeColors.errorMain)
                      : getThemeColor(theme, MUIThemeColors.black),
                  }}
                />
                <ColumnNameText
                  isPlaceholder={!column.fieldName}
                  color={getThemeColor(theme, MUIThemeColors.errorDark)}
                >
                  {column.fieldName || translate('select-field-placeholder', { ns: 'dynamic-report' })}
                </ColumnNameText>
              </ColumnHeaderLeft>
              <ColumnHeaderActions>
                {!isEditing && (
                  <Tooltip title={`${translate('edit-column', { ns: 'dynamic-report' })}`}>
                    <IconButton size="small" onClick={() => onEditColumn(column)}>
                      <EditOutlined fontSize="small" sx={{ color: 'text.secondary' }} />
                    </IconButton>
                  </Tooltip>
                )}
                {isEditing && (
                  <Tooltip title={`${translate('cancel', { ns: 'common' })}`}>
                    <IconButton size="small" onClick={onCancelEdit}>
                      <CloseOutlined fontSize="small" sx={{ color: 'text.secondary' }} />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip
                  title={`${translate(isColumnMissing ? 'column-field-not-available' : 'delete-column', {
                    ns: 'dynamic-report',
                  })}`}
                >
                  <IconButton size="small" onClick={() => handleDeleteColumnClick(column)}>
                    <DeleteOutline fontSize="small" sx={{ color: 'error.main' }} />
                  </IconButton>
                </Tooltip>
              </ColumnHeaderActions>
            </ColumnCardHeader>

            {isEditing && (
              <ColumnEditBody>
                <ColumnFieldRow>
                  <ColumnFieldLabel>
                    {translate('column-name', { ns: 'dynamic-report' })}
                    <RequiredAsterisk>*</RequiredAsterisk>
                  </ColumnFieldLabel>
                  <Select
                    size="small"
                    fullWidth
                    value={workingColumn.formSchemaFieldId ?? ''}
                    onChange={(e: SelectChangeEvent<string | ''>) => {
                      if (e.target.value !== '') onDraftFieldSelect(e.target.value)
                    }}
                    displayEmpty
                    renderValue={selected => {
                      if (selected === '') {
                        return (
                          <PlaceholderText>
                            {translate('select-field-placeholder', { ns: 'dynamic-report' })}
                          </PlaceholderText>
                        )
                      }
                      const field = schemaColumns?.find(f => f.identifier === selected)
                      return field?.name ?? column.fieldName ?? String(selected)
                    }}
                  >
                    {availableFieldsForDraft.map(field => (
                      <MenuItem key={field.identifier} value={field.identifier}>
                        {field.name}
                      </MenuItem>
                    ))}
                  </Select>
                </ColumnFieldRow>

                {!hideWidthConfig && (
                  <AppTextField
                    size="small"
                    fullWidth
                    type="number"
                    inputProps={{ min: 20, max: 500 }}
                    label={translate('column-width', { ns: 'dynamic-report' })}
                    value={workingColumn.width !== null ? String(workingColumn.width) : ''}
                    placeholder={translate('auto', { ns: 'dynamic-report' })}
                    onChange={onDraftWidthChange}
                    error={
                      workingColumn.width !== null &&
                      workingColumn.width !== undefined &&
                      (workingColumn.width < 20 || workingColumn.width > 500)
                    }
                    helperText={
                      workingColumn.width !== null &&
                      workingColumn.width !== undefined &&
                      (workingColumn.width < 20 || workingColumn.width > 500)
                        ? translate('column-width-range-error', { ns: 'dynamic-report' })
                        : undefined
                    }
                  />
                )}

                <Button
                  variant="contained"
                  size="small"
                  fullWidth
                  onClick={onSaveColumn}
                  disabled={
                    !draftColumn?.formSchemaFieldId ||
                    (draftColumn?.width !== null &&
                      draftColumn?.width !== undefined &&
                      (draftColumn.width < 20 || draftColumn.width > 500))
                  }
                  sx={{ mt: 0.5, textTransform: 'none', fontWeight: 600 }}
                >
                  {translate('save-column', { ns: 'dynamic-report' })}
                </Button>
              </ColumnEditBody>
            )}
          </ColumnCard>
        )
      })}

      {isAddingNewColumn && draftColumn && (
        <AddNewColumnCard
          columns={columns}
          draftColumn={draftColumn}
          availableFieldsForDraft={availableFieldsForDraft}
          schemaColumns={schemaColumns}
          hideWidthConfig={hideWidthConfig}
          onCancelEdit={onCancelEdit}
          onDeleteColumn={handleDeleteColumnClick}
          onDraftFieldSelect={onDraftFieldSelect}
          onDraftWidthChange={onDraftWidthChange}
          onSaveColumn={onSaveColumn}
        />
      )}
    </ColumnSection>
  )
}
